import { BlobApi, DEBUG_WARN } from '@/util'
import { defineStore } from 'pinia'   
import { computed, reactive, ref } from 'vue'

export interface IPictureData {
    pictureHasLoaded: boolean,
    pictureData: string | null,
    pictureDimensions: {
        width: number,
        height: number
    }
}

const maxQueueSize = 1000

/** cache for loaded pictures */
export const usePictureCache = defineStore('pictureCache', () => {
    /********************************************************************************************************************
     STATE
     ********************************************************************************************************************/
    const _dict: Map<string,IPictureData> = new Map()
    const _queue: IPictureData[] = []
    const _activeFetches = ref(0)

    const state = { _dict, _queue, _activeFetches }

    /********************************************************************************************************************
     GETTERS
     ********************************************************************************************************************/
    /** number of currently pending picture loading calls to the backend */
    const activeFetches = computed(() => _activeFetches.value)

    const getters = { activeFetches }

    /********************************************************************************************************************
     ACTIONS
     ********************************************************************************************************************/
    function getImage(blobName: string): IPictureData {
        let pictureData = _dict.get(blobName)

        if (!pictureData) {
            pictureData = fetchImage(blobName)
            _dict.set(blobName, pictureData)
            _queue.push(pictureData)

            if (_queue.length > maxQueueSize) {
                _queue.shift()
            }
        }

        return pictureData
    }
     
    const actions = { getImage }

    /********************************************************************************************************************
     PRIVATE
     ********************************************************************************************************************/
    function fetchImage(blobName: string): IPictureData {
        const result: IPictureData = reactive({
            pictureHasLoaded: false,
            pictureData: null,
            pictureDimensions: {
                width: 0,
                height: 0
            }
        })

        _activeFetches.value++
        const api = new BlobApi
        api.downloadBlob(blobName)
            .then(response => {
                const blob = new Blob([response.data], { type: response.headers['content-type'] })
                return imageToRawData(blobName, blob)
            })
            .then(rawData => {
                result.pictureHasLoaded = true
                result.pictureData = rawData
                return result
            })
            .then(image => getImageDimensions(image.pictureData ?? ''))
            .then(dimensions => result.pictureDimensions = dimensions)
            .catch(error => DEBUG_WARN('PictureCache: Failed to fetch image:', error))
            .finally(() => _activeFetches.value--)

        return result
    }

    function imageToRawData(blobName: string, blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()

            reader.onloadend = () => {
                if (reader.result) {
                    resolve(reader.result as string)
                } else {
                    reject(new Error(`Failed to read blob ${blobName} as Base64 string`))
                }
            }

            reader.onerror = () => {
                reject(new Error(`Error reading blob ${blobName}`))
            }

            reader.readAsDataURL(blob)
        })
    }

    function getImageDimensions(imageData: string): Promise<{ width: number, height: number }> {
        return new Promise((resolve, reject) => {
            const img = new Image()
            img.onload = () => {
                resolve({ width: img.naturalWidth, height: img.naturalHeight })
            }
            img.onerror = reject
            img.src = imageData
        })
    }

    /********************************************************************************************************************
     MISC
     ********************************************************************************************************************/    
    return { ...state, ...getters, ...actions }
})